import { customerStatusCopy, RECOMMENDATION_COPY, uniquePlainBlockers } from "./taskCustomerCopy";
import { canonicalizeSection, currentActionSection, TASK_PRIMARY_SECTIONS } from "./taskSections";

/**
 * One customer-facing Task workspace projection.
 *
 * Prefer the server orchestration snapshot when present. Fall back to the
 * frozen Task + latest review so the header cannot invent a second lifecycle.
 * Components must not reinterpret status independently.
 */

export const TASK_WORKSPACE_SECTIONS = TASK_PRIMARY_SECTIONS.map((item) => ({ ...item }));

export const TASK_WORKSPACE_STAGES = [
  "Planning",
  "Ready for handoff",
  "Waiting for engineering",
  "In progress",
  "Ready for review",
  "Waiting for decision",
  "Completed",
  "Needs attention",
];

export const TASK_WORKSPACE_STATUSES = [
  "In progress",
  "Ready",
  "Waiting",
  "Blocked",
  "Stale",
  "Failed",
  "Complete",
];

const ROLE_LABELS = {
  product: "Product",
  engineering: "Engineering",
  reviewer: "Reviewer",
  control_owner: "Administrator",
  administrator: "Administrator",
  none: "No one",
};

const PHASE_TO_STAGE = {
  capturing_request: "Planning",
  clarifying_decisions: "Planning",
  agreeing_handoff: "Ready for handoff",
  implementing: "In progress",
  collecting_evidence: "Ready for review",
  awaiting_decision: "Waiting for decision",
  decided: "Completed",
  merge_ready: "Completed",
  returned_for_rework: "Needs attention",
};

const RECOMMENDATION_LABELS = RECOMMENDATION_COPY;

function ownerLabel(role) {
  return ROLE_LABELS[role] || "Someone";
}

function uniqueBlockers(items) {
  const seen = new Set();
  const out = [];
  (items || []).forEach((item) => {
    if (!item) return;
    const reasonCode = String(item.reasonCode || item.code || item.detail || item.text || "");
    const key = `${reasonCode}:${item.detail || item.text || ""}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({
      reasonCode: item.reasonCode || item.code || null,
      text: item.detail || item.text || item.reason || reasonCode,
      owner: item.owner ? ownerLabel(item.owner) : item.ownerLabel || null,
      outsideRenderRepo: Boolean(item.outsideRenderRepo),
    });
  });
  return out;
}

function reviewAllowedEvents(review) {
  return Array.isArray(review?.flow?.allowedEvents) ? review.flow.allowedEvents : [];
}

function reviewIsStale(review) {
  const stale = String(review?.staleState || "").toLowerCase();
  return Boolean(review) && stale && stale !== "fresh";
}

function reviewHumanDecided(review) {
  return Boolean(review?.decidedAt || review?.frozenHandover || review?.reviewVerdict === "accepted" || review?.reviewVerdict === "accepted_conditionally" || review?.reviewVerdict === "rejected");
}

function fallbackFromPlanAndReview(input) {
  const planStatus = String(input.planStatus || "").toLowerCase();
  const contractState = input.contractState || null;
  const hasEngAck = Boolean(input.hasEngAck) || contractState === "engineering_acknowledged";
  const review = input.latestReview || null;
  const openQuestionCount = Number(input.openQuestionCount) || 0;
  const planFailed = ["failed", "rejected"].includes(planStatus);

  if (planFailed) {
    return {
      stage: "Needs attention",
      status: "Failed",
      statusLabel: planStatus === "rejected" ? "Needs revision" : "Planning failed",
      statusExplanation: "Planning did not finish. Revise the request or retry.",
      owner: { role: "product", label: "Product" },
      primaryAction: { id: "revise_plan", label: "Revise Task", section: "task" },
      defaultSection: "task",
      editingAllowed: true,
    };
  }

  if (review && reviewHumanDecided(review)) {
    const verdict = String(review.reviewVerdict || "");
    if (verdict === "rejected") {
      return {
        stage: "Needs attention",
        status: "Complete",
        statusLabel: "Returned for rework",
        statusExplanation: "A reviewer rejected this delivery. Address the findings and request a new review.",
        owner: { role: "engineering", label: "Engineering" },
        primaryAction: { id: "review_findings", label: "Review findings", section: "review" },
        defaultSection: "review",
        editingAllowed: false,
      };
    }
    return {
      stage: "Completed",
      status: "Complete",
      statusLabel: "Completed",
      statusExplanation: "A reviewer recorded a decision on this delivery.",
      owner: { role: "none", label: "No one" },
      primaryAction: null,
      defaultSection: "task",
      editingAllowed: false,
    };
  }

  if (review && reviewIsStale(review)) {
    return {
      stage: "Needs attention",
      status: "Stale",
      statusLabel: "The latest work needs review",
      statusExplanation: "The work changed after the last review. The previous review no longer describes the latest version.",
      owner: { role: "reviewer", label: "a reviewer" },
      primaryAction: { id: "review_latest_version", label: "Review latest version", section: "delivery" },
      defaultSection: "delivery",
      editingAllowed: false,
    };
  }

  if (review && (review.status === "pending" || review.status === "running")) {
    return {
      stage: "Ready for review",
      status: "In progress",
      statusLabel: "Review running",
      statusExplanation: "RenderRepo is gathering evidence for this delivery.",
      owner: { role: "none", label: "RenderRepo" },
      primaryAction: { id: "review_findings", label: "View review progress", section: "review" },
      defaultSection: "review",
      editingAllowed: false,
    };
  }

  if (review && review.status === "failed") {
    return {
      stage: "Needs attention",
      status: "Failed",
      statusLabel: "Review failed",
      statusExplanation: "The last Delivery Review could not finish. Review the error and try again.",
      owner: { role: "reviewer", label: "Reviewer" },
      primaryAction: { id: "rerun_review", label: "Try review again", section: "review" },
      defaultSection: "review",
      editingAllowed: false,
    };
  }

  if (review && review.status === "completed") {
    const events = reviewAllowedEvents(review);
    const acceptBlocked = !events.includes("HUMAN_ACCEPT");
    const check = review.checkContract || {};
    const awaiting =
      check.conclusion === "awaiting_human_decision" ||
      review.flowState === "awaiting_human_decision" ||
      review.flow?.state === "awaiting_human_decision";
    if (acceptBlocked || awaiting) {
      return {
        stage: "Waiting for decision",
        status: "Blocked",
        statusLabel: "Waiting for test results",
        statusExplanation: "The work has been reviewed, but it cannot be approved yet.",
        owner: { role: "reviewer", label: "Independent reviewer" },
        primaryAction: { id: "view_review", label: "View review", section: "review" },
        defaultSection: "review",
        editingAllowed: false,
      };
    }
    return {
      stage: "Waiting for decision",
      status: "Waiting",
      statusLabel: "Waiting for a decision",
      statusExplanation: "The review is ready. A reviewer can now decide.",
      owner: { role: "reviewer", label: "Reviewer" },
      primaryAction: { id: "record_decision", label: "Make decision", section: "review" },
      defaultSection: "review",
      editingAllowed: false,
    };
  }

  if (hasEngAck) {
    return {
      stage: "Ready for review",
      status: "Waiting",
      statusLabel: "Ready for review",
      statusExplanation: "Engineering acknowledged the frozen Task. Select the delivered revision to review.",
      owner: { role: "reviewer", label: "Reviewer" },
      primaryAction: { id: "connect_delivered_work", label: "Connect delivered work", section: "delivery" },
      defaultSection: "delivery",
      editingAllowed: false,
    };
  }

  if (contractState === "product_approved") {
    return {
      stage: "Waiting for engineering",
      status: "Waiting",
      statusLabel: "Waiting for engineering",
      statusExplanation: "Product approved this Task. Engineering still needs to acknowledge it.",
      owner: { role: "engineering", label: "Engineering" },
      primaryAction: { id: "acknowledge", label: "Acknowledge as engineer", section: "engineering" },
      defaultSection: "engineering",
      editingAllowed: false,
    };
  }

  if (input.intentFreezeReady || (input.hasDraft && openQuestionCount === 0 && input.approvalAllowed)) {
    return {
      stage: "Ready for handoff",
      status: "Ready",
      statusLabel: "Ready for handoff",
      statusExplanation: "The Task is ready for product approval.",
      owner: { role: "product", label: "Product" },
      primaryAction: { id: "approve_task", label: "Review Task", section: "task" },
      defaultSection: "task",
      editingAllowed: true,
    };
  }

  if (input.hasDraft && openQuestionCount > 0) {
    return {
      stage: "Planning",
      status: "Waiting",
      statusLabel: "Planning",
      statusExplanation: "A high-value question still needs an answer before this Task can be frozen.",
      owner: { role: "product", label: "Product" },
      primaryAction: { id: "answer_question", label: "Answer next question", section: "conversation" },
      defaultSection: "conversation",
      editingAllowed: true,
    };
  }

  if (["generating", "pending"].includes(planStatus) && !input.hasDraft) {
    return {
      stage: "Planning",
      status: "In progress",
      statusLabel: "Planning",
      statusExplanation: "RenderRepo is preparing this Task from the request and attached sources.",
      owner: { role: "none", label: "RenderRepo" },
      primaryAction: null,
      defaultSection: "task",
      editingAllowed: true,
    };
  }

  return {
    stage: "Planning",
    status: "In progress",
    statusLabel: "Planning",
    statusExplanation: "Start from the original request and attached sources.",
    owner: { role: "product", label: "Product" },
        primaryAction: { id: "continue_plan", label: "Continue talking", section: "conversation" },
      defaultSection: "conversation",
      editingAllowed: true,
  };
}

function fromSnapshot(snapshot, fallback) {
  const phase = snapshot.furthestPhaseReached || snapshot.phase;
  let stage = PHASE_TO_STAGE[phase] || fallback.stage;
  const effective = snapshot.currentEffectiveState || "";
  let status = fallback.status;
  let statusLabel = fallback.statusLabel;
  let statusExplanation = snapshot.phaseExplanation || fallback.statusExplanation;
  let ownerRole = snapshot.nextAction?.responsibleRole || fallback.owner?.role;
  const blockers = uniqueBlockers(snapshot.blockingReasons);

  if (phase === "agreeing_handoff") {
    if (fallback.stage === "Waiting for engineering") stage = "Waiting for engineering";
    else if (fallback.stage === "Ready for handoff") stage = "Ready for handoff";
  }

  if (effective === "blocked_on_administration") {
    status = "Blocked";
    if (phase === "awaiting_decision" || fallback.stage === "Waiting for decision") {
      stage = "Waiting for decision";
      statusLabel = "Waiting for review requirements";
      statusExplanation =
        blockers.length > 0
          ? blockers.map((item) => item.text).join(" ")
          : "Two things are required before anyone can decide: a passing test run on this commit and an independent reviewer.";
    } else {
      statusLabel = "Needs administrator setup";
    }
  } else if (effective === "blocked_on_review") {
    status = "Blocked";
    statusLabel = "Waiting for a reviewer";
  } else if (effective === "blocked_on_product") {
    status = "Blocked";
    statusLabel = stage === "Planning" ? "Planning" : "Waiting on product";
  } else if (effective === "blocked_on_engineering") {
    status = "Waiting";
    statusLabel = "Waiting for engineering";
  } else if (effective === "cleared_to_merge") {
    status = "Complete";
    statusLabel = "Completed";
  } else if (effective === "progressing") {
    status = fallback.status === "Ready" && stage !== "Ready for handoff" ? "In progress" : fallback.status;
  }

  if (snapshot.stale || snapshot.staleFromPhase) {
    status = "Stale";
    statusLabel = "The latest work needs review";
    stage = "Needs attention";
  }
  if (snapshot.superseded) {
    status = "Stale";
    statusLabel = "Superseded";
    stage = "Needs attention";
  }

  if (status === "Blocked" && /ready/i.test(statusLabel) && !/handoff/i.test(statusLabel)) {
    statusLabel = "Waiting for review requirements";
  }

  return {
    stage,
    status,
    statusLabel,
    statusExplanation,
    owner: { role: ownerRole, label: ownerLabel(ownerRole) },
    blockers,
    snapshotNextAction: snapshot.nextAction || null,
  };
}

function actionsForStage(stage, status) {
  switch (stage) {
    case "Waiting for decision":
      return {
        defaultSection: "review",
        primaryAction:
          status === "Blocked"
            ? { id: "view_review", label: "View review", section: "review" }
            : { id: "record_decision", label: "Make decision", section: "review" },
        editingAllowed: false,
      };
    case "Ready for review":
      return {
        defaultSection: "delivery",
        primaryAction: { id: "connect_delivered_work", label: "Connect delivered work", section: "delivery" },
        editingAllowed: false,
      };
    case "Needs attention":
      return {
        defaultSection: "delivery",
        primaryAction:
          status === "Stale"
            ? { id: "review_latest_version", label: "Review latest version", section: "delivery" }
            : { id: "review_findings", label: "View review", section: "review" },
        editingAllowed: false,
      };
    case "Completed":
      return { defaultSection: "review", primaryAction: null, editingAllowed: false };
    case "Waiting for engineering":
      return {
        defaultSection: "engineering",
        primaryAction: { id: "acknowledge", label: "Acknowledge as engineer", section: "engineering" },
        editingAllowed: false,
      };
    case "Ready for handoff":
      return {
        defaultSection: "task",
        primaryAction: { id: "approve_task", label: "Review Task", section: "task" },
        editingAllowed: true,
      };
    case "In progress":
      return {
        defaultSection: "engineering",
        primaryAction: { id: "view_handoff", label: "View engineering handoff", section: "engineering" },
        editingAllowed: false,
      };
    default:
      return {
        defaultSection: "conversation",
        primaryAction: { id: "continue_plan", label: "Continue talking", section: "conversation" },
        editingAllowed: true,
      };
  }
}

function sectionState(id, projection) {
  const currentId = currentActionSection(projection);
  const completed = {
    conversation: ["Ready for handoff", "Waiting for engineering", "In progress", "Ready for review", "Waiting for decision", "Completed"].includes(projection.stage),
    task: ["Ready for handoff", "Waiting for engineering", "In progress", "Ready for review", "Waiting for decision", "Completed"].includes(projection.stage),
    engineering: ["In progress", "Ready for review", "Waiting for decision", "Completed"].includes(projection.stage),
    delivery: Boolean(
      ["Ready for review", "Waiting for decision", "Completed"].includes(projection.stage) &&
        projection.status !== "Stale"
    ),
    review: ["Waiting for decision", "Completed"].includes(projection.stage),
  };
  const blocked = projection.status === "Blocked" && currentId === id;
  let tone = "available";
  if (id === currentId) tone = blocked ? "blocked" : "current";
  else if (completed[id]) tone = "complete";
  if (projection.status === "Stale" && (id === "delivery" || id === "review")) tone = "needs_attention";
  if (projection.stage === "Needs attention" && id === currentId) tone = "needs_attention";
  return {
    id,
    available: true,
    current: id === currentId,
    complete: completed[id] && tone === "complete",
    needsAttention: tone === "needs_attention" || (blocked && id === currentId),
    blocked: tone === "blocked",
    tone,
    currentSection: id === currentId,
  };
}

export function projectTaskWorkspace(input = {}) {
  const fallback = fallbackFromPlanAndReview(input);
  const snapshot = input.orchestrationSnapshot || null;
  const fromServer = snapshot ? fromSnapshot(snapshot, fallback) : null;

  const stage = fromServer?.stage || fallback.stage;
  const status = fromServer?.status || fallback.status;
  const copy = customerStatusCopy(stage, status);
  const statusLabel = copy.statusLabel;
  const statusExplanation = copy.statusExplanation;
  const owner = copy.owner;
  const whatHappensNext = copy.whatHappensNext;
  const blockers = uniquePlainBlockers([
    ...(fromServer?.blockers || []),
    ...(input.blockers || []),
  ]);

  const review = input.latestReview || null;
  const events = reviewAllowedEvents(review);
  const recommendation = review?.handover?.recommendation || null;
  const reviewRecommendation = recommendation
    ? {
        key: recommendation,
        label: RECOMMENDATION_LABELS[recommendation] || recommendation,
        headline: review.handover?.headline || "",
        summary: review.handover?.summary || "",
        humanDecisionRecorded: reviewHumanDecided(review),
      }
    : null;

  const decisionEligibility = {
    canAccept: events.includes("HUMAN_ACCEPT"),
    canAcceptConditionally: events.includes("HUMAN_ACCEPT_CONDITIONAL"),
    canReject: events.includes("HUMAN_REJECT"),
    canRerun: events.includes("RERUN_REQUESTED"),
    allowedEvents: events,
    blockedReason:
      review && !events.includes("HUMAN_ACCEPT") && review.status === "completed" && !reviewHumanDecided(review)
        ? "You can’t approve this yet."
        : null,
  };

  const derivedActions = actionsForStage(stage, status);
  const rawAction = copy.primaryAction || derivedActions.primaryAction;
  const primaryAction = rawAction
    ? { ...rawAction, section: canonicalizeSection(rawAction.section) || derivedActions.defaultSection }
    : null;
  const secondaryActions = [];
  if (review) {
    secondaryActions.push({ id: "view_evidence", label: "View evidence", section: "activity" });
  }
  if (fallback.editingAllowed && stage !== "Planning") {
    secondaryActions.push({ id: "revise_task", label: "Create a revision", section: "task" });
  }
  secondaryActions.push({ id: "view_history", label: "View history", section: "activity" });

  const completedMilestones = [];
  if (["Ready for handoff", "Waiting for engineering", "In progress", "Ready for review", "Waiting for decision", "Completed"].includes(stage)) {
    completedMilestones.push({
      id: "plan",
      label: "Plan agreed",
      summary: input.promiseCount
        ? `${input.promiseCount} requirement${input.promiseCount === 1 ? "" : "s"}`
        : "Requirements confirmed",
    });
  }
  if (["In progress", "Ready for review", "Waiting for decision", "Completed"].includes(stage)) {
    completedMilestones.push({
      id: "handoff",
      label: "Handoff complete",
      summary: input.frozenVersionNumber
        ? `Engineering acknowledged version ${input.frozenVersionNumber}`
        : "Engineering acknowledged",
    });
  }
  if (review && review.status === "completed") {
    completedMilestones.push({
      id: "review",
      label: "Delivery Review ready",
      summary: reviewRecommendation?.label || "Findings available",
    });
  }

  const workflowProgress = [
    { id: "plan", label: "Planning", state: completedMilestones.some((m) => m.id === "plan") ? "complete" : stage === "Planning" ? "current" : "upcoming" },
    { id: "handoff", label: "Handoff", state: completedMilestones.some((m) => m.id === "handoff") ? "complete" : ["Ready for handoff", "Waiting for engineering"].includes(stage) ? "current" : "upcoming" },
    { id: "engineering", label: "Engineering", state: ["Ready for review", "Waiting for decision", "Completed"].includes(stage) ? "complete" : stage === "In progress" ? "current" : "upcoming" },
    { id: "review", label: "Delivery review", state: stage === "Completed" ? "complete" : ["Ready for review", "Waiting for decision", "Needs attention"].includes(stage) ? (status === "Blocked" ? "needs_attention" : "current") : "upcoming" },
    { id: "decision", label: "Decision", state: stage === "Completed" ? "complete" : stage === "Waiting for decision" ? "waiting" : "upcoming" },
  ];

  const projection = {
    stage,
    status,
    statusLabel,
    statusExplanation,
    owner,
    primaryAction,
    secondaryActions,
    blockers,
    completedMilestones,
    workflowProgress,
    availableSections: TASK_WORKSPACE_SECTIONS.map((section) => section.id),
    defaultSection: canonicalizeSection(primaryAction?.section || derivedActions.defaultSection) || "task",
    whatHappensNext,
    reviewRecommendation,
    decisionEligibility,
    evidenceLimitations: review?.handover?.residualRisks || [],
    editingAllowed: Boolean(derivedActions.editingAllowed) && status !== "Stale",
    decisionControlsAllowed: Boolean(
      status !== "Blocked" &&
        review &&
        review.status === "completed" &&
        !reviewIsStale(review) &&
        !reviewHumanDecided(review) &&
        (decisionEligibility.canAccept ||
          decisionEligibility.canAcceptConditionally ||
          decisionEligibility.canReject)
    ),
    source: snapshot ? "server_snapshot" : "client_fallback",
  };

  projection.sectionStates = Object.fromEntries(
    TASK_WORKSPACE_SECTIONS.map((section) => [section.id, sectionState(section.id, projection)])
  );

  return projection;
}

export function readTaskSectionHash(hash = typeof window !== "undefined" ? window.location.hash : "") {
  const match = String(hash || "").match(/task-section=([a-z]+)/i);
  return canonicalizeSection(match?.[1]) || null;
}

export function writeTaskSectionHash() {
  // Path + Task id are canonical. A leftover hash must not select a section.
}

export function clearTaskSectionHash() {
  if (typeof window === "undefined") return;
  if (!/task-section=/i.test(window.location.hash || "")) return;
  window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
}

export function workspaceRegionHidden(workspaceSection, region) {
  if (!workspaceSection) return false;
  const current = canonicalizeSection(workspaceSection);
  const target = canonicalizeSection(region);
  if (!current || !target) return workspaceSection !== region;
  return current !== target;
}

export function formatSafeDisplayText(value, fallback = "") {
  if (value == null) return fallback;
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (typeof value === "object") {
    if (typeof value.text === "string") return value.text;
    if (typeof value.label === "string") return value.label;
    if (typeof value.summary === "string") return value.summary;
    if (typeof value.name === "string") return value.name;
  }
  return fallback;
}

export function uxHeuristicViolations(view = {}) {
  const violations = [];
  const statuses = view.canonicalStatusLabels || [];
  if (statuses.length > 1) {
    violations.push({ code: "multiple_canonical_statuses", detail: statuses.join(", ") });
  }
  if (statuses.includes("Ready") && statuses.some((label) => /blocked/i.test(label))) {
    violations.push({ code: "contradictory_ready_blocked" });
  }
  if ((view.primaryActionCount || 0) > 1) {
    violations.push({ code: "multiple_primary_actions" });
  }
  if ((view.duplicateBlockerCount || 0) > 0) {
    violations.push({ code: "duplicated_blockers" });
  }
  if (view.completedEditorsMountedInReview) {
    violations.push({ code: "completed_editors_in_review" });
  }
  if (view.rawObjectStringPresent) {
    violations.push({ code: "raw_object_stringification" });
  }
  if (view.technicalIdsInDefaultPmView) {
    violations.push({ code: "technical_ids_in_default_view" });
  }
  if (view.decisionControlsShownWhileDisallowed) {
    violations.push({ code: "decision_controls_disallowed" });
  }
  return violations;
}
